import React, { useCallback, useEffect, useMemo, useState } from "react";
import SafeIcon from "../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import { useClient } from "../../context/ClientContext";
import { clientDocumentService } from "../../services/client/clientDocumentService";
import ClientDocumentFilters from "../../components/clients/documents/ClientDocumentFilters";
import ClientDocumentTable from "../../components/clients/documents/ClientDocumentTable";
import ClientDocumentUploadModal from "../../components/clients/modals/ClientDocumentUploadModal";
import ClientDocumentPreviewModal from "../../components/clients/modals/ClientDocumentPreviewModal";
import ClientDocumentDetailsModal from "../../components/clients/modals/ClientDocumentDetailsModal";

const PAGE_SIZE = 15;
const EMPTY_SUMMARY = { total: 0, shared: 0, private: 0, recent: 0 };
const DEFAULT_FILTERS = { search: "", category: "all", visibility: "all", sortDirection: "desc" };

function SummaryCard({ label, value, note, icon, tone }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3"><div className="min-w-0"><p className="text-sm font-medium text-slate-500">{label}</p><p className="mt-2 text-2xl font-black tracking-tight text-slate-900">{value.toLocaleString()}</p><p className="mt-1 text-xs text-slate-400">{note}</p></div><span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}><SafeIcon icon={icon} className="text-lg" /></span></div>
    </section>
  );
}

function triggerDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || "document";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ClientDocuments() {
  const { activeWorkspace, user } = useClient();
  const organizationId = activeWorkspace?.id || null;
  const organizationName = activeWorkspace?.name || "Your business";
  const role = String(activeWorkspace?.role || "").toLowerCase();
  const canManage = role === "owner" || role === "client";
  const currentUserName = [user?.user_metadata?.first_name, user?.user_metadata?.last_name].filter(Boolean).join(" ") || user?.email || "You";

  const [documents, setDocuments] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loadedOrganizationId, setLoadedOrganizationId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [detailsDocument, setDetailsDocument] = useState(null);
  const [busyDocumentId, setBusyDocumentId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const resetOrganizationView = useCallback(() => {
    setDocuments([]);
    setSummary(EMPTY_SUMMARY);
    setLoadedOrganizationId(null);
    setTotalCount(0);
    setPage(1);
    setFilters(DEFAULT_FILTERS);
    setUploadOpen(false);
    setPreviewDocument(null);
    setDetailsDocument(null);
    setBusyDocumentId(null);
  }, []);

  const requestOptions = useMemo(() => ({
    organizationId,
    page,
    pageSize: PAGE_SIZE,
    ...filters,
  }), [organizationId, page, filters]);

  useEffect(() => {
    let current = true;
    resetOrganizationView();
    setError("");
    setNotice("");

    if (!organizationId) {
      setLoading(false);
      setError("Select a business to view its documents.");
      return () => { current = false; };
    }

    setLoading(true);
    Promise.all([
      clientDocumentService.getClientDocuments({ ...requestOptions, page: 1 }),
      clientDocumentService.getClientDocumentSummary({ organizationId }),
    ]).then(([list, stats]) => {
      if (!current) return;
      setDocuments(list.documents);
      setTotalCount(list.totalCount);
      setSummary(stats);
      setLoadedOrganizationId(organizationId);
      setPage(1);
    }).catch((loadError) => {
      if (current) setError(loadError?.message || "Unable to load business documents.");
    }).finally(() => {
      if (current) setLoading(false);
    });

    return () => { current = false; };
  }, [organizationId, resetOrganizationView, reloadKey]);

  useEffect(() => {
    if (!organizationId || loadedOrganizationId !== organizationId || page === 1) return undefined;
    let current = true;
    setLoading(true);
    clientDocumentService.getClientDocuments(requestOptions).then((list) => {
      if (current && organizationId === activeWorkspace?.id) {
        setDocuments(list.documents);
        setTotalCount(list.totalCount);
      }
    }).catch((loadError) => {
      if (current) setError(loadError?.message || "Unable to load this document page.");
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [requestOptions, page, loadedOrganizationId, activeWorkspace?.id, organizationId]);

  const refresh = async () => {
    if (!organizationId || refreshing) return;
    setRefreshing(true);
    setError("");
    try {
      const [list, stats] = await Promise.all([
        clientDocumentService.getClientDocuments({ ...requestOptions, page: 1 }),
        clientDocumentService.getClientDocumentSummary({ organizationId }),
      ]);
      if (organizationId !== activeWorkspace?.id) return;
      setDocuments(list.documents);
      setTotalCount(list.totalCount);
      setSummary(stats);
      setLoadedOrganizationId(organizationId);
      setPage(1);
    } catch (refreshError) {
      setError(refreshError?.message || "Unable to refresh documents.");
    } finally {
      setRefreshing(false);
    }
  };

  const upload = async (values) => {
    await clientDocumentService.uploadClientDocument(values);
    setNotice("Document uploaded successfully.");
    setReloadKey((key) => key + 1);
  };

  const download = async (document) => {
    if (!organizationId || busyDocumentId) return;
    setBusyDocumentId(document.id);
    setError("");
    try {
      const blob = await clientDocumentService.downloadClientDocument({ organizationId, document });
      triggerDownload(blob, document.file_name);
    } catch (downloadError) {
      setError(downloadError?.message || "Secure download failed.");
    } finally {
      setBusyDocumentId(null);
    }
  };

  const archive = async (document) => {
    if (!organizationId || !canManage || busyDocumentId) return;
    if (!window.confirm(`Archive “${document.file_name}”? The file will be retained.`)) return;
    setBusyDocumentId(document.id);
    setError("");
    try {
      await clientDocumentService.archiveClientDocument({ organizationId, documentId: document.id });
      setNotice("Document archived.");
      setReloadKey((key) => key + 1);
    } catch (archiveError) {
      setError(archiveError?.message || "This document could not be archived.");
    } finally {
      setBusyDocumentId(null);
    }
  };

  const updateDetails = async () => {
    setReloadKey((key) => key + 1);
  };

  const changeFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
  };

  const showingData = loadedOrganizationId === organizationId;
  const displayedDocuments = showingData ? documents : [];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-xs font-bold uppercase tracking-widest text-violet-700">Collaboration</p><h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Documents</h1><p className="mt-2 text-sm text-slate-600">Securely manage and share your business documents.</p></div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Active business</p><p className="mt-1 max-w-xs truncate text-sm font-bold text-slate-800">{organizationName}</p></div>
          <button type="button" onClick={refresh} disabled={!organizationId || refreshing || loading} className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50"><SafeIcon icon={FiIcons.FiRefreshCw} className={refreshing ? "animate-spin" : ""} /><span className="hidden sm:inline">Refresh</span></button>
          <button type="button" onClick={() => setUploadOpen(true)} disabled={!organizationId} className="inline-flex h-11 items-center gap-2 rounded-xl bg-violet-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 disabled:opacity-50"><SafeIcon icon={FiIcons.FiUpload} />Upload Document</button>
        </div>
      </header>

      {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"><div className="flex items-start gap-2"><SafeIcon icon={FiIcons.FiAlertTriangle} className="mt-0.5 shrink-0" /><div><p className="font-semibold">Documents could not be loaded or updated</p><p className="mt-1">{error}</p></div></div></div>}
      {notice && <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">{notice}</div>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Total Documents" value={showingData ? summary.total : 0} note="Active business files" icon={FiIcons.FiFolder} tone="bg-violet-50 text-violet-700" />
        <SummaryCard label="Shared with Accountant" value={showingData ? summary.shared : 0} note="Available to authorized accountants" icon={FiIcons.FiShare2} tone="bg-emerald-50 text-emerald-700" />
        <SummaryCard label="Private Documents" value={showingData ? summary.private : 0} note="Visible only to this business" icon={FiIcons.FiLock} tone="bg-blue-50 text-blue-700" />
        <SummaryCard label="Recently Uploaded" value={showingData ? summary.recent : 0} note="Uploaded in the last seven days" icon={FiIcons.FiClock} tone="bg-amber-50 text-amber-700" />
      </div>

      <ClientDocumentFilters
        {...filters}
        onChange={changeFilter}
        onReset={() => { setFilters(DEFAULT_FILTERS); setPage(1); }}
      />

      <ClientDocumentTable
        documents={displayedDocuments}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        currentUserId={user?.id}
        currentUserName={currentUserName}
        canManage={canManage}
        busyDocumentId={busyDocumentId}
        onPageChange={setPage}
        onPreview={setPreviewDocument}
        onDownload={download}
        onDetails={setDetailsDocument}
        onArchive={archive}
      />

      <ClientDocumentUploadModal open={uploadOpen} organizationId={organizationId} onClose={() => setUploadOpen(false)} onUpload={upload} />
      <ClientDocumentPreviewModal organizationId={organizationId} document={previewDocument} onClose={() => setPreviewDocument(null)} onDownload={download} />
      <ClientDocumentDetailsModal organizationId={organizationId} document={detailsDocument} currentUserId={user?.id} canManage={canManage} onClose={() => setDetailsDocument(null)} onSaved={updateDetails} />
    </div>
  );
}